// src/components/onboarding/steps/GetStartedStep.tsx

import { useEffect, useRef } from "react";
import { Box, Button, Card, Stack, Text, ThemeIcon } from "@mantine/core";
import {
  IconCheck,
  IconBuilding,
  IconDatabase,
  IconPlugConnected,
} from "@tabler/icons-react";
import { useBranding } from "@/context/BrandingContext";

type Props = {
  industry: string | null;
  dwhStatus: string | null;
  dwhProjectId: string | null;
  dwhStorageMode: string | null;
  selectedSources: string[];
  onGetStarted: () => void;
};

export default function GetStartedStep({
  industry,
  dwhStatus,
  dwhProjectId,
  dwhStorageMode,
  selectedSources,
  onGetStarted,
}: Props) {
  const { branding } = useBranding();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Simple confetti effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
      size: number;
      life: number;
    }> = [];

    const colors = [
      "#3b82f6", "#10b981", "#06b6d4", "#8b5cf6",
      "#22c55e", "#0ea5e9", "#6366f1",
    ];

    // Create particles
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -10 - Math.random() * canvas.height * 0.5,
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 2 + 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 2,
        life: 1,
      });
    }

    let animFrame: number;
    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let alive = false;
      for (const p of particles) {
        if (p.life <= 0) continue;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.03; // gravity
        p.life -= 0.003;

        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size * 0.6);
      }
      ctx.globalAlpha = 1;

      if (alive) {
        animFrame = requestAnimationFrame(animate);
      }
    }

    animate();
    return () => cancelAnimationFrame(animFrame);
  }, []);

  const dwhLabel =
    dwhStorageMode === "VERITY_MANAGED"
      ? `${branding.productName} Managed`
      : dwhStorageMode === "CUSTOMER_OWNED"
        ? "Bring Your Own"
        : "Not configured";

  const dwhStatusLabel =
    dwhStatus === "ACTIVE"
      ? "Active"
      : dwhStatus === "PROVISIONING"
        ? "Provisioning..."
        : dwhStatus || "Pending";

  return (
    <Box style={{ position: "relative", overflow: "hidden" }}>
      {/* Confetti canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <Stack gap="lg" style={{ position: "relative", zIndex: 2 }}>
        <Box>
          <Text size="xl" fw={700}>
            4 — Get Started
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            Here's a summary of your setup. You can always change these later.
          </Text>
        </Box>

        <Stack gap="md">
          {/* Industry */}
          <Card padding="md" radius="md" withBorder>
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <ThemeIcon size={36} radius="md" variant="light" color="blue">
                <IconBuilding size={18} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" fw={500}>
                  Industry
                </Text>
                <Text size="sm" fw={600}>
                  {industry
                    ? industry.charAt(0).toUpperCase() + industry.slice(1).replace(/_/g, " ")
                    : "Not selected"}
                </Text>
              </Box>
              {industry && (
                <ThemeIcon size={24} radius="xl" variant="light" color="green">
                  <IconCheck size={14} />
                </ThemeIcon>
              )}
            </Box>
          </Card>

          {/* DWH */}
          <Card padding="md" radius="md" withBorder>
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <ThemeIcon size={36} radius="md" variant="light" color="blue">
                <IconDatabase size={18} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" fw={500}>
                  Data Warehouse
                </Text>
                <Text size="sm" fw={600}>
                  {dwhLabel} (Status: {dwhStatusLabel}
                  {dwhProjectId ? `, ID: ${dwhProjectId}` : ""})
                </Text>
              </Box>
              {dwhStatus === "ACTIVE" && (
                <ThemeIcon size={24} radius="xl" variant="light" color="green">
                  <IconCheck size={14} />
                </ThemeIcon>
              )}
            </Box>
          </Card>

          {/* Sources */}
          <Card padding="md" radius="md" withBorder>
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <ThemeIcon size={36} radius="md" variant="light" color="blue">
                <IconPlugConnected size={18} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" fw={500}>
                  Sources
                </Text>
                <Text size="sm" fw={600}>
                  {selectedSources.length > 0
                    ? `${selectedSources.length} selected (${selectedSources.join(", ")})`
                    : "None selected"}
                </Text>
              </Box>
              {selectedSources.length > 0 && (
                <ThemeIcon size={24} radius="xl" variant="light" color="green">
                  <IconCheck size={14} />
                </ThemeIcon>
              )}
            </Box>
          </Card>
        </Stack>

        <Button
          size="lg"
          fullWidth
          variant="gradient"
          gradient={{ from: "blue", to: "cyan" }}
          onClick={onGetStarted}
          mt="md"
        >
          Get Started with {branding.productName}
        </Button>
      </Stack>
    </Box>
  );
}
